import { CommonModule } from '@angular/common';
import { Component, inject, signal } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { PublicRestService } from '@generated/be';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { typySablonOptions } from '@reza-ais-fe/reza-ais-fe-utils';
import {
  AutocompleteComponent,
  DatepickerRangeComponent,
  InputComponent,
  IOption,
  SelectComponent,
} from '@shared/ui/forms';
import { Logger, LoggerService, publicApiArgs, SkipLoading } from '@shared/utils';
import { AutoCompleteCompleteEvent } from 'primeng/autocomplete';
import { BehaviorSubject, catchError, of, Subject, switchMap } from 'rxjs';
import { SeznamSablonFilterFormService } from '../../../services/seznam-sablon-filter-form.service';
import { HttpContext } from '@angular/common/http';
import { Cy_Katalog } from '@features/cypress';

@UntilDestroy()
@Component({
  selector: 'reza-seznam-sablon-filter',
  standalone: true,
  imports: [
    CommonModule,
    InputComponent,
    DatepickerRangeComponent,
    ReactiveFormsModule,
    SelectComponent,
    AutocompleteComponent,
  ],
  templateUrl: './seznam-sablon-filter.component.html',
  styleUrl: './seznam-sablon-filter.component.scss',
})
export class SablonyFilterComponent {
  readonly filterForm = inject(SeznamSablonFilterFormService).filterForm;
  readonly publicRestService = inject(PublicRestService);
  readonly loggerService = inject(LoggerService);
  private logger: Logger = this.loggerService.createLogger('SeznamSablonFilterComponent');
  readonly Cy_Katalog = Cy_Katalog;

  statusOptions = signal<IOption[]>([]);

  triggerSearchIdentifikator$ = new Subject<string>();
  triggerSearchNazev$ = new Subject<string>();
  triggerSearchGestor$ = new Subject<string>();

  identifikatorResults = signal<string[]>([]);
  nazevResults = signal<string[]>([]);
  gestorResults = signal<string[]>([]);

  kodyOvm$ = new BehaviorSubject<string[]>([]);

  typOptions = typySablonOptions;

  constructor() {
    // Nezobrazování loadingu pro určitě operace (nyní jen na našeptávačích)
    const context = new HttpContext().set(SkipLoading, true);

    this.triggerSearchIdentifikator$
      .asObservable()
      .pipe(
        untilDestroyed(this),
        switchMap((query) => {
          return this.publicRestService
            .naseptejIdentifikatorPublicSablony({ textInputDto: { text: query } }, ...publicApiArgs(context))
            .pipe(catchError(() => of([])));
        })
      )
      .subscribe((result: any) => {
        this.identifikatorResults.set(result.seznam ?? []);
      });

    this.triggerSearchNazev$
      .asObservable()
      .pipe(
        untilDestroyed(this),
        switchMap((query) => {
          return this.publicRestService
            .naseptejNazevPublicSablony(
              { textInputDto: { text: query }, ...publicApiArgs() },
              ...publicApiArgs(context)
            )
            .pipe(catchError(() => of([])));
        })
      )
      .subscribe((result: any) => {
        const nazevArray = (result.content || []).map((item: any) => ({
          ...item,
          showField: `${item.nazev ?? ''} - ${item.kod ?? ''}`,
        }));

        this.nazevResults.set(nazevArray ?? []);
      });

    this.triggerSearchGestor$
      .asObservable()
      .pipe(
        untilDestroyed(this),
        switchMap((query) => {
          return this.publicRestService
            .naseptejGestoraPublicSablony({ textInputDto: { text: query } }, ...publicApiArgs(context))
            .pipe(catchError(() => of([])));
        })
      )
      .subscribe((result: any) => {
        const gestors = (result.seznamObjekt || []).map((item: any) => ({
          ...item,
          showField: `${item.kod ?? ''} - ${item.nazev ?? ''}`,
        }));
        this.gestorResults.set(gestors ?? []);
      });
  }

  /**
   * Identifikátor šablony autocomplete search
   */
  handleIdentifikatorSearch(event: AutoCompleteCompleteEvent) {
    this.logger.debug('', event);
    if (this.filterForm.controls.interniId.invalid) {
      return;
    }

    this.triggerSearchIdentifikator$.next(event.query);
  }

  /**
   * Název šablony autocomplete search
   */
  handleNazevSearch(event: AutoCompleteCompleteEvent) {
    this.logger.debug('', event);
    if (this.filterForm.controls.nazev.invalid) {
      return;
    }

    this.triggerSearchNazev$.next(event.query);
  }

  /**
   * Gestor šablony autocomplete search
   */
  handleGestorSearch(event: AutoCompleteCompleteEvent) {
    this.logger.debug('', event);
    if (this.filterForm.controls.nazev.invalid) {
      return;
    }

    this.triggerSearchGestor$.next(event.query);
  }
}
